import { Document, Link, Text, View } from '@react-pdf/renderer'
import { PageFooter } from 'features/reporting/pdfGeneration/components/shared/PageFooter'
import { PageHeader } from 'features/reporting/pdfGeneration/components/shared/PageHeader'
import { PdfFieldRows } from 'features/reporting/pdfGeneration/components/shared/PdfFieldRows'
import { PDF_HEADING_MIN_PRESENCE } from 'features/reporting/pdfGeneration/components/shared/pdfStyles'
import { PdfTable } from 'features/reporting/pdfGeneration/components/shared/PdfTable'
import {
  ReportPage,
  type ReportPageProps,
} from 'features/reporting/pdfGeneration/components/shared/ReportPage'
import { sanitizeForPdf } from 'features/reporting/pdfGeneration/utils/formatters'
import { Fragment } from 'react'

import { WorkPackPdfDrawing } from './WorkPackPdfDrawing'
import { workPackPdfStyles as styles } from './workPackPdfStyles'
import { PlanningWorkPack, WorkPackImage } from './workPackTypes'

export const PlanningWorkPackDocument = ({
  pack,
  images,
  pageNumbers = {},
  onChapterPage,
}: {
  pack: PlanningWorkPack
  images: WorkPackImage[]
  pageNumbers?: Record<string, number>
  onChapterPage?: (id: string, page: number) => void
}) => {
  const pageProps: Omit<ReportPageProps, 'children'> = {
    size: 'A4',
    pageStyle: styles.page,
    contentStyle: styles.content,
    header: <PageHeader title={pack.title} />,
    footer: <PageFooter revisionNumber={pack.version} />,
  }
  return (
    <Document title={pack.title} subject={pack.ticketId}>
      <ReportPage {...pageProps}>
        <Text style={styles.coverTitle}>{sanitizeForPdf(pack.title)}</Text>
        <Text style={styles.note}>{sanitizeForPdf(pack.purpose)}</Text>
        <Text
          style={[styles.heading, styles.sectionHeading]}
          minPresenceAhead={PDF_HEADING_MIN_PRESENCE}>
          {sanitizeForPdf(pack.cover.title)}
        </Text>
        <PdfFieldRows rows={pack.cover.rows} layout='stacked' />
      </ReportPage>
      <ReportPage {...pageProps}>
        <Text style={styles.title}>{sanitizeForPdf(pack.contentsTitle)}</Text>
        {pack.chapters.map((chapter) => (
          <View key={chapter.id} style={styles.tocRow} wrap={false}>
            <Link src={`#${chapter.id}`} style={styles.tocLabel}>
              {sanitizeForPdf(chapter.title)}
            </Link>
            <Text style={styles.tocNumber}>
              {pageNumbers[chapter.id] ?? '-'}
            </Text>
          </View>
        ))}
      </ReportPage>
      {pack.chapters.map((chapter) => {
        const image = chapter.drawing
          ? images.find((item) => item.id === chapter.drawing?.id)
          : undefined
        if (chapter.drawing && !image)
          throw new Error(`Missing work-pack drawing ${chapter.drawing.id}`)
        return (
          <ReportPage
            {...pageProps}
            key={chapter.id}
            orientation={chapter.drawing ? 'landscape' : 'portrait'}>
            <View wrap={false} minPresenceAhead={45}>
              <Text id={chapter.id} style={styles.title}>
                {sanitizeForPdf(chapter.title)}
              </Text>
              <Text
                render={({ pageNumber, totalPages }) => {
                  if (totalPages) onChapterPage?.(chapter.id, pageNumber)
                  return ''
                }}
              />
            </View>
            {chapter.sections.map((section, index) => (
              <Fragment key={index}>
                <Text
                  style={[styles.heading, styles.sectionHeading]}
                  minPresenceAhead={PDF_HEADING_MIN_PRESENCE}>
                  {sanitizeForPdf(section.title)}
                </Text>
                <PdfFieldRows rows={section.rows} layout='stacked' />
                {section.tables?.map((table, tableIndex) => (
                  <Fragment key={tableIndex}>
                    <Text
                      style={styles.heading}
                      minPresenceAhead={PDF_HEADING_MIN_PRESENCE}>
                      {sanitizeForPdf(table.title)}
                    </Text>
                    <PdfTable
                      table={{
                        headers: table.columns.map((column) => column.title),
                        rows: table.records.map((record) => ({
                          cells: table.columns.map(
                            (column) => record[column.key]
                          ),
                        })),
                      }}
                    />
                  </Fragment>
                ))}
              </Fragment>
            ))}
            {chapter.drawing && image && (
              <WorkPackPdfDrawing drawing={chapter.drawing} image={image} />
            )}
          </ReportPage>
        )
      })}
    </Document>
  )
}
